import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Backdrop, CircularProgress, useTheme } from '@mui/material'
import { Hub } from 'aws-amplify/utils'
import {
  confirmResetPassword,
  confirmSignUp,
  fetchAuthSession,
  resetPassword,
  resendSignUpCode,
  signIn,
  signOut,
  signUp,
  updateUserAttributes
} from 'aws-amplify/auth'
import { useCsrf } from './CsrfContext'
import { clearStoredDestinations } from '../auth-redirect'
import { assertNoAuthErrorPayload, extractAuthErrorMessage } from '../auth-errors'
import { clearConsultantSession } from '../consultant-info-session'
import { clearStoredConfirmEmailState } from '../confirm-email-storage'
import {
  clearInstitutionalAccessRetry,
  readInstitutionalAccessRetry,
  storeInstitutionalAccessRetry
} from '../institutional-access-retry'
import {
  clearPreferredUsernamePending,
  isBusinessEmailAddress,
  readPreferredUsernamePending,
  storePreferredUsernamePending
} from '../preferred-username-pending'
import {
  clearRegistrationAuthError,
  clearRegistrationProgress,
  clearRegistrationState,
  storeRegistrationAuthError
} from '../registration-state'

const AuthContext = createContext({})
export const useAuth = () => useContext(AuthContext)
const isBrowser = typeof window !== 'undefined'
const INSTITUTIONAL_GROUP = 'institutional_access'
export const ACCESS_STATUS_INSTITUTIONAL = 'institutional'
const LOGOUT_REDIRECT_PATH = '/institutional/login/'

const COGNITO_STORAGE_KEY_PATTERNS = [
  'CognitoIdentityServiceProvider',
  'amplify-signin-with-hostedUI',
  'aws-amplify',
  'LastAuthUser'
]

const isAmplifyAuthStorageKey = (key) => (
  key === null ||
  COGNITO_STORAGE_KEY_PATTERNS.some((pattern) => String(key).includes(pattern))
)

const expireCookie = (name, options = {}) => {
  if (!isBrowser || !name) return

  const path = options.path ? `; path=${options.path}` : ''
  const domain = options.domain ? `; domain=${options.domain}` : ''
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''

  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; Max-Age=0${path}${domain}; SameSite=Lax${secure}`
}

const clearBrowserLogoutState = () => {
  if (!isBrowser) return

  // The register-pending cookie included: it outlives the tab that set it, so a sign-out that
  // left it behind handed the next registration on this browser the previous one's address.
  clearRegistrationState()
  clearStoredDestinations()
  clearStoredConfirmEmailState()

  clearConsultantSession()

  expireCookie('userExperience')
  expireCookie('userExperience', { path: '/' })
  expireCookie('userExperience', { path: '/', domain: window.location.hostname })
}

const parseBody = async (response) => {
  const text = await response.text().catch(() => '')
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

const parseGroups = (raw) => {
  if (!raw) return []
  if (Array.isArray(raw)) return raw
  return String(raw)
    .split(',')
    .map((g) => g.trim())
    .filter(Boolean)
}

const mergeGroups = (...claims) =>
  Array.from(new Set(claims.flatMap((claim) => parseGroups(claim))))

const getLoadingBackdropSx = (theme) => ({
  color: '#fff',
  zIndex: theme?.zIndex?.modal ? theme.zIndex.modal + 1 : 1400,
  backgroundColor: 'rgba(9,42,72,.4)'
})

const isRetryableRequestError = (error) => {
  if (!error) return false
  if (error.body?.retryable === true) return true
  const status = Number(error.status)
  if (Number.isNaN(status)) return true
  return status >= 500 || status === 408 || status === 429
}

export const hasInstitutionalGroup = (candidate) => Boolean(
  Array.isArray(candidate?.groups) && candidate.groups.includes(INSTITUTIONAL_GROUP)
)

/**
 * Whether there is anything left for the deferred retry to finish. Not proof of what the
 * account can see - only `institutional_access` on a freshly issued token shows that, via
 * `hasInstitutionalGroup`, and that is what the UI routes on.
 *
 * A 200 from the access endpoint is not a grant: the upstream can answer `pending` on the
 * institutional route, and a 409 comes back as an `ok` with nothing granted behind it.
 * `access_contact_pending` gets no vote either - whether the CRM has converted the lead to
 * a contact has nothing to do with the access role.
 */
const isInstitutionalGrant = (response) => {
  if (!response) return false

  // The group is the grant, and the group endpoint is the only one that reports it, so
  // its verdict is the whole answer. The CRM fields below cannot see group membership.
  if (typeof response.access_group_ensured === 'boolean') return response.access_group_ensured

  // An endpoint that never adds the group cannot report a grant, however institutional
  // the role it recorded.
  if (response.access_group_pending === true) return false

  return response.access_status === ACCESS_STATUS_INSTITUTIONAL &&
    response.access_conflict !== true &&
    response.access_deferred !== true
}

const resolveSignupUserId = (result) => (
  result?.userSub ||
  result?.userId ||
  result?.UserSub ||
  result?.UserId ||
  result?.sub ||
  result?.user?.userSub ||
  result?.user?.userId ||
  ''
)

const readUserFromSession = async (options = {}) => {
  const session = await fetchAuthSession(options)
  const tokens = session?.tokens
  const idToken = tokens?.idToken
  const accessToken = tokens?.accessToken
  const idPayload = idToken?.payload || {}
  const accessPayload = accessToken?.payload || {}
  const sub = idPayload.sub || accessPayload.sub || ''
  if (!sub) return null

  return {
    email: idPayload.email || accessPayload.email || '',
    given_name: idPayload.given_name || accessPayload.given_name || '',
    family_name: idPayload.family_name || accessPayload.family_name || '',
    preferred_username: idPayload.preferred_username || accessPayload.preferred_username || '',
    sub,
    groups: mergeGroups(
      idPayload['cognito:groups'],
      accessPayload['cognito:groups']
    ),
    exp: accessPayload.exp || idPayload.exp,
    idToken: idToken?.toString?.(),
    accessToken: accessToken?.toString?.()
  }
}

const buildUserFromSession = async (options = {}) => {
  try {
    return await readUserFromSession(options)
  } catch (err) {
    return null
  }
}

const Loading = () => {
  const t = useTheme()
  return <Backdrop open sx={getLoadingBackdropSx(t)}><CircularProgress size={48} thickness={4} /></Backdrop>
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [hydrated, setHydrated] = useState(false)
  const [loadingCount, setLoadingCount] = useState(0)
  const loading = loadingCount > 0
  const { ensure } = useCsrf()

  const beginLoading = useCallback(() => setLoadingCount((c) => c + 1), [])
  const endLoading = useCallback(() => setLoadingCount((c) => (c > 0 ? c - 1 : 0)), [])

  const applyUser = useCallback((nextUser) => setUser(nextUser || null), [])

  const api = useCallback(async (path, { method = 'GET', body, csrf = false, signal, retry = true, bearer } = {}) => {
    const runRequest = async (forceCsrf = false) => {
      const headers = {}
      const serializedBody = body === undefined ? undefined : JSON.stringify(body)
      if (serializedBody !== undefined) {
        headers['Content-Type'] = 'application/json'
      }
      if (csrf && method !== 'GET') {
        headers['x-csrf-token'] = await ensure(forceCsrf)
      }
      // Proof of session for endpoints that act on the account rather than merely
      // filing a request against it.
      if (bearer) headers.Authorization = `Bearer ${bearer}`

      const response = await fetch(`${path}`, {
        method,
        credentials: 'include',
        headers,
        body: serializedBody,
        signal
      })
      const payload = await parseBody(response)
      return { response, payload }
    }

    let { response, payload } = await runRequest()
    const csrfFailed = response.status === 401 &&
      csrf &&
      retry !== false &&
      method !== 'GET' &&
      typeof payload === 'object' &&
      payload !== null &&
      payload.error === 'Bad CSRF'

    if (csrfFailed) {
      const retryResult = await runRequest(true)
      response = retryResult.response
      payload = retryResult.payload
    }

    if (!response.ok) {
      const detail = typeof payload === 'object' && payload !== null ? (payload.error || payload.message) : null
      const error = new Error(detail || response.statusText || 'Request failed')
      error.status = response.status
      error.body = payload
      throw error
    }
    return typeof payload === 'object' && payload !== null ? payload : {}
  }, [ensure])

  const sync = useCallback(async (options = {}) => {
    const { fallbackToCached = false, ...sessionOptions } = options
    let nextUser = null

    try {
      nextUser = await readUserFromSession(sessionOptions)
    } catch {
      if (fallbackToCached && sessionOptions.forceRefresh) {
        nextUser = await buildUserFromSession()
      }
    }

    applyUser(nextUser)
    return nextUser
  }, [applyUser])

  const refresh = useCallback(async (options = {}) => sync({
    forceRefresh: true,
    fallbackToCached: options.fallbackToCached !== false
  }), [sync])
  const verify = useCallback(async () => sync(), [sync])

  /**
   * Records a proven business address as preferred_username - the attribute the
   * site reads as "business e-mail on file". Never throws: the caller's flow is
   * already complete by the time this runs, and a failure only means the marker
   * is left for the next session.
   */
  const applyBusinessEmailMarker = useCallback(async (email, options = {}) => {
    const { userId = '', skipRefresh = false, attempts = 0 } = options
    const normalized = typeof email === 'string' ? email.trim() : ''
    if (!userId || !isBusinessEmailAddress(normalized)) return false

    try {
      await updateUserAttributes({ userAttributes: { preferred_username: normalized } })
      // Only this account's marker is done. A shared browser can hold one for
      // another account that has not signed in yet.
      const pending = readPreferredUsernamePending()
      if (!pending || pending.userId === userId) clearPreferredUsernamePending()
      // The attribute is read off the token, so nothing sees it until a fresh one.
      if (!skipRefresh) await refresh({ fallbackToCached: true }).catch(() => undefined)
      return true
    } catch (error) {
      // No usable session yet, or the pool treats the attribute as an alias that
      // is already taken. Neither is worth failing anything over - keep the
      // marker and let a later session finish the job.
      console.warn('Unable to record business email as preferred_username', error)
      storePreferredUsernamePending({ email: normalized, userId, attempts })
      return false
    }
  }, [refresh])

  const login = useCallback(async (email, password) => {
    beginLoading()
    try {
      clearStoredConfirmEmailState()
      await signIn({
        username: email,
        password,
        options: { authFlowType: 'USER_PASSWORD_AUTH' }
      })
      const u = await sync({ forceRefresh: true, fallbackToCached: true })
      if (!u) throw new Error('Unable to verify login session')
      return u
    } finally {
      endLoading()
    }
  }, [beginLoading, endLoading, sync])

  const oAuthLogin = useCallback(async () => {
    beginLoading()
    try {
      clearStoredConfirmEmailState()
      const u = await sync({ forceRefresh: true, fallbackToCached: true })
      if (!u) throw new Error('Unable to complete OAuth login')
      return u
    } finally {
      endLoading()
    }
  }, [beginLoading, endLoading, sync])

  const logout = useCallback(async () => {
    beginLoading()
    const initialHref = isBrowser ? window.location.href : ''
    let shouldRedirectToLogin = false
    try {
      applyUser(null)
      clearBrowserLogoutState()
      try {
        await signOut({ global: true })
      } catch {
        await signOut()
      }
    } catch {
      /* ignore sign out errors */
    } finally {
      applyUser(null)
      clearBrowserLogoutState()
      endLoading()
      shouldRedirectToLogin = isBrowser && window.location.href === initialHref
    }

    if (shouldRedirectToLogin) {
      window.location.replace(LOGOUT_REDIRECT_PATH)
    }
  }, [applyUser, beginLoading, endLoading])

  const requestInstitutionalAccess = useCallback(async (email, userId, domainStatus, options = {}) => {
    const { silent = false } = options
    if (!silent) beginLoading()
    try {
      const response = await api('/api/cognito-register-access', {
        method: 'POST',
        body: {
          business_email: email,
          user_id: userId,
          domain: domainStatus
        },
        csrf: true
      })
      assertNoAuthErrorPayload(response, 'Unable to request institutional access right now. Please try again.')
      return response
    } finally {
      if (!silent) endLoading()
    }
  }, [api, beginLoading, endLoading])

  /**
   * Finishes institutional access for a registration whose e-mail the just-consumed setup
   * code already proved, so a recognised business address never has to be re-entered on
   * the confirm-business-email page.
   *
   * The access token is sent because the endpoint acts on the account - it marks the
   * address verified upstream and adds the group - which it may not do for anyone but the
   * caller who owns it. `silent` is for the deferred retry, which runs on an ordinary page
   * load and must not put a full-screen spinner over it.
   */
  const registerVerify = useCallback(async (email, userId, { silent = false } = {}) => {
    if (!silent) beginLoading()
    try {
      let response
      try {
        const session = await fetchAuthSession().catch(() => null)
        const accessToken = session?.tokens?.accessToken?.toString()
        if (!accessToken) {
          throw new Error('A signed-in session is required to confirm your business email.')
        }

        response = await api('/api/cognito-register-verify', {
          method: 'POST',
          body: { business_email: email, user_id: userId },
          csrf: true,
          bearer: accessToken
        })
        assertNoAuthErrorPayload(response, 'Unable to confirm your business email right now.')
      } catch (error) {
        // Two of the ways this fails - no access token, and a CSRF fetch that could not
        // complete - happen before any request leaves the browser, so the server has no
        // record of them. Arm the retry, name the failure, and let it propagate: a caller
        // that turns this into `null` cannot tell "the server said no" from "we never
        // asked".
        storeInstitutionalAccessRetry({ email, userId })
        console.error('Business email verification could not complete', {
          status: error?.status ?? null,
          reason: error?.body?.error || error?.message || String(error)
        })
        throw error
      }

      // Keep the deferred retry armed unless the grant actually landed: a proven
      // domain whose group has not arrived is precisely what that net is for.
      if (response?.domain_status === 'valid') {
        if (isInstitutionalGrant(response)) clearInstitutionalAccessRetry()
        else storeInstitutionalAccessRetry({ email, userId })
      }

      return response
    } finally {
      if (!silent) endLoading()
    }
  }, [api, beginLoading, endLoading])

  const signup = useCallback(async (email, givenName, familyName, domainStatus, password) => {
    beginLoading()
    clearRegistrationAuthError()
    clearRegistrationProgress()
    try {
      const normalizedGivenName = typeof givenName === 'string' ? givenName.trim() : ''
      const normalizedFamilyName = typeof familyName === 'string' ? familyName.trim() : ''
      if (!normalizedGivenName || !normalizedFamilyName) {
        throw new Error('First name and last name are required to create your account.')
      }

      // preferred_username - the business e-mail of record - is deliberately not
      // written here. The only verdict available at this point is the browser's,
      // which may have been computed for a different address or not have finished
      // at all, and an attribute written into signUp() cannot be taken back once
      // the server disagrees. The proven verdict arrives with the access request
      // below and is carried to the first session by the pending marker instead.
      const attrs = {
        email,
        given_name: normalizedGivenName,
        family_name: normalizedFamilyName
      }

      const result = await signUp({
        username: email,
        password,
        options: {
          userAttributes: attrs,
          autoSignIn: true
        }
      })
      assertNoAuthErrorPayload(result, 'Unable to create your account right now. Please try again.')

      const resolvedUserId = resolveSignupUserId(result)
      if (!resolvedUserId) {
        throw new Error('Unable to complete registration without a user id')
      }
      let accessRequest = null
      let accessError = null
      // Only a payload that passed the error check counts as a grant: an
      // error-shaped 200 must not read as success and skip the deferred retry.
      const attemptAccess = async () => {
        const response = await requestInstitutionalAccess(email, resolvedUserId, domainStatus)
        assertNoAuthErrorPayload(response, 'Unable to request institutional access right now. Please try again.')
        return response
      }
      try {
        accessRequest = await attemptAccess()
      } catch (firstError) {
        // One immediate retry, then hand the account to the sign-in retry so a
        // failed grant can never leave a registered user with no access record.
        if (isRetryableRequestError(firstError)) {
          try {
            accessRequest = await attemptAccess()
          } catch (secondError) {
            accessError = secondError
          }
        } else {
          accessError = firstError
        }
      }

      // The server's verdict is the only one that counts, for the business e-mail
      // and for what the grant was supposed to be.
      const provenDomainStatus = accessRequest?.domain_status || ''
      const expectedInstitutional = provenDomainStatus === 'valid'
      // The upstream was asked for institutional and answered with something short of it.
      // Deliberately narrower than "not granted": no registration is granted at this
      // point, because this endpoint cannot add the group, and reporting that ordinary
      // state as a problem would tell every qualifying registrant something went wrong.
      const accessDeclined = Boolean(accessRequest) &&
        expectedInstitutional &&
        (accessRequest.access_status !== ACCESS_STATUS_INSTITUTIONAL ||
          accessRequest.access_deferred === true ||
          accessRequest.access_conflict === true)

      if (accessError || !accessRequest || accessDeclined) {
        console.error('Institutional access was not granted during signup', accessError || {
          domainStatus: provenDomainStatus,
          accessStatus: accessRequest?.access_status || '',
          accessConflict: accessRequest?.access_conflict === true,
          upstreamHttpStatus: accessRequest?.upstream_http_status
        })
      }

      // Armed for every registration that expects institutional access, not just the ones
      // that went wrong: the group needs the session and proven address only the confirm
      // step has, so no qualifying registration has access yet. Clearing the marker on a
      // response that merely filed a lead is what left accounts with an Institutional
      // record, an approval e-mail and nothing the site would show them. The confirm step
      // clears it as soon as the group is on a token.
      if (accessError || !accessRequest || accessDeclined || expectedInstitutional) {
        storeInstitutionalAccessRetry({ email, userId: resolvedUserId })
      } else {
        clearInstitutionalAccessRetry()
      }

      // preferred_username needs a user-pool session, and registration has none
      // until the password is set, so queue the proven address for the first one
      // rather than leaving a verified account with no business e-mail on file.
      if (expectedInstitutional && isBusinessEmailAddress(email)) {
        storePreferredUsernamePending({ email, userId: resolvedUserId })
      }

      clearRegistrationAuthError()

      return {
        userSub: resolvedUserId,
        userId: resolvedUserId,
        UserSub: resolvedUserId,
        UserConfirmed: Boolean(
          result?.UserConfirmed ??
          result?.userConfirmed ??
          result?.isSignUpComplete ??
          result?.nextStep?.signUpStep === 'DONE'
        ),
        access_request: accessRequest,
        access_error: accessError
          ? {
            message: extractAuthErrorMessage(accessError, 'Unable to request institutional access right now.'),
            retryable: isRetryableRequestError(accessError)
          }
          : null,
        access_status: accessRequest?.access_status || '',
        access_unconfirmed: accessDeclined,
        expected_institutional: expectedInstitutional,
        domain_status: accessRequest?.domain_status || domainStatus,
        nextStep: result?.nextStep || null
      }
    } catch (error) {
      const message = extractAuthErrorMessage(error, 'Unable to create your account right now. Please try again.')
      storeRegistrationAuthError(message, email)
      clearRegistrationProgress()
      const wrappedError = new Error(message)
      wrappedError.cause = error
      throw wrappedError
    } finally {
      endLoading()
    }
  }, [beginLoading, endLoading, requestInstitutionalAccess])

  const signupReset = useCallback(async (code, email) => {
    beginLoading()
    try {
      await confirmSignUp({ username: email, confirmationCode: code })
      clearRegistrationProgress()
      return await sync({ forceRefresh: true, fallbackToCached: true })
    } finally {
      endLoading()
    }
  }, [beginLoading, endLoading, sync])

  const forgotPasswordInit = useCallback(async (email) => {
    beginLoading()
    try {
      return await resetPassword({ username: email })
    } finally {
      endLoading()
    }
  }, [beginLoading, endLoading])

  const resendSignupCode = useCallback(async (email) => {
    beginLoading()
    try {
      await resendSignUpCode({ username: email })
      return {}
    } finally {
      endLoading()
    }
  }, [beginLoading, endLoading])

  const forgotPassword = useCallback(async (code, email, password) => {
    beginLoading()
    try {
      await confirmResetPassword({ username: email, confirmationCode: code, newPassword: password })
      return {}
    } finally {
      endLoading()
    }
  }, [beginLoading, endLoading])

  const requestReset = useCallback((email) => forgotPasswordInit(email), [forgotPasswordInit])
  const confirmReset = useCallback((email, code, password) => forgotPassword(code, email, password), [forgotPassword])

  const confirmEmail = useCallback(async (email, id, domainState) => {
    beginLoading()
    try {
      await api('/api/cognito-confirm-email', { method: 'POST', body: { business_email: email, user_id: id, domain: domainState }, csrf: true })
      return await sync({ forceRefresh: true, fallbackToCached: true })
    } finally {
      endLoading()
    }
  }, [api, beginLoading, endLoading, sync])

  const confirmEmailNoSync = useCallback(async (email, id, domainState) => {
    beginLoading()
    try {
      return await api('/api/cognito-confirm-email', { method: 'POST', body: { business_email: email, user_id: id, domain: domainState }, csrf: true })
    } finally {
      endLoading()
    }
  }, [api, beginLoading, endLoading])

  const confirmCode = useCallback(async (id, token, businessEmail, domainState) => {
    beginLoading()
    try {
      const body = { user_id: id, token }
      const normalizedEmail = typeof businessEmail === 'string' ? businessEmail.trim() : ''
      if (normalizedEmail) body.business_email = normalizedEmail
      if (domainState === 'valid' || domainState === 'invalid') body.domain = domainState
      // A CSRF rejection happens before the token is spent, so retrying with a
      // fresh CSRF token is safe - and stops a stale token from burning the link.
      const payload = await api('/api/cognito-confirm-code', { method: 'POST', body, csrf: true })

      // The confirmation is the server proving the address, so this is where a
      // business e-mail can be recorded without guessing. The caller re-syncs the
      // session immediately afterwards, so skip the extra token refresh.
      if (payload?.ok && payload.domain_status === 'valid') {
        const confirmedEmail = typeof payload.email === 'string' && payload.email ? payload.email : normalizedEmail
        await applyBusinessEmailMarker(confirmedEmail, {
          userId: payload.user_id || id,
          skipRefresh: true
        })
      }

      return { payload }
    } finally {
      endLoading()
    }
  }, [api, applyBusinessEmailMarker, beginLoading, endLoading])

  useEffect(() => {
    let cancelled = false

    const hydrate = async () => {
      const initialUser = await buildUserFromSession()
      if (cancelled) return
      applyUser(initialUser)
      setHydrated(true)

      if (initialUser) {
        let refreshedUser = null
        try {
          refreshedUser = await readUserFromSession({ forceRefresh: true })
        } catch {
          refreshedUser = await buildUserFromSession()
        }
        if (!cancelled && refreshedUser) applyUser(refreshedUser)
      }
    }

    hydrate()

    const unsub = Hub.listen('auth', ({ payload }) => {
      const event = payload?.event
      if (['signedOut', 'signOut'].includes(event)) {
        applyUser(null)
        return
      }
      if (['signIn', 'signedIn', 'signInWithRedirect', 'autoSignIn', 'autoSignInSuccess'].includes(event)) {
        sync({ forceRefresh: true, fallbackToCached: true })
        return
      }
      if (event === 'tokenRefresh') {
        sync()
        return
      }
      if (['tokenRefresh_failure', 'signInWithRedirect_failure'].includes(event)) {
        sync()
      }
    })

    const onStorage = (event) => {
      if (event.storageArea && event.storageArea !== window.localStorage) return
      if (!isAmplifyAuthStorageKey(event.key)) return
      sync()
    }

    if (isBrowser) window.addEventListener('storage', onStorage)

    return () => {
      cancelled = true
      unsub()
      if (isBrowser) window.removeEventListener('storage', onStorage)
    }
  }, [applyUser, sync])

  const access = useMemo(() => ({
    groups: user?.groups || [],
    // Two different questions. Institutional access is a group on the token; being
    // signed in is not, and pages such as user settings exist precisely for the
    // account that is signed in and still waiting on that group.
    isAuthenticated: Boolean(user?.sub),
    isInstitutional: hasInstitutionalGroup(user)
  }), [user])

  const accessRetryRef = useRef(false)

  // Safety net for a registration whose access request never landed: finish it on
  // the next authenticated session instead of leaving the account with no record.
  useEffect(() => {
    if (!isBrowser || accessRetryRef.current) return
    if (!user?.sub) return

    const marker = readInstitutionalAccessRetry()
    if (!marker || marker.userId !== user.sub) return

    if (access.isInstitutional) {
      clearInstitutionalAccessRetry()
      return
    }

    accessRetryRef.current = true
    const attempts = marker.attempts + 1

    // registerVerify, not the registration endpoint: what is missing is the group, and the
    // registration endpoint cannot add one, so a net built on it was re-filing a CRM
    // record and calling it done. This runs with a session in hand, which is what the
    // grant requires, and the endpoint re-derives the domain itself so nothing here can
    // grant what the server has not proven. The registration endpoint stays as the
    // fallback: if verification cannot run at all, filing the request at least keeps the
    // account visible to review.
    const finishAccess = async () => {
      try {
        return await registerVerify(marker.email, marker.userId, { silent: true })
      } catch (verifyError) {
        console.warn('Deferred business email verification failed; filing the access request instead', verifyError)
        return requestInstitutionalAccess(marker.email, marker.userId, undefined, { silent: true })
      }
    }

    finishAccess()
      .then(async (response) => {
        // This response carries the server's domain verdict and arrives with a
        // session in hand - the one moment a registration that never got a verdict
        // can still have its business e-mail recorded.
        if (response?.domain_status === 'valid' && !user.preferred_username) {
          await applyBusinessEmailMarker(marker.email, { userId: marker.userId, skipRefresh: true })
        }

        // Group membership only reaches the browser on a fresh token, and the one this
        // session started with cannot show a grant that landed a moment ago.
        const refreshedUser = await refresh({ fallbackToCached: true }).catch(() => null)

        // The group is the proof; the response body is only what the upstream said it
        // recorded. Accepting either keeps the net armed for an account that genuinely has
        // nothing and disarms it for one that is already through.
        const expectedInstitutional = response?.domain_status === 'valid'
        const granted = hasInstitutionalGroup(refreshedUser) ||
          (Boolean(response?.ok) && (!expectedInstitutional || isInstitutionalGrant(response)))

        if (granted) clearInstitutionalAccessRetry()
        else storeInstitutionalAccessRetry({ ...marker, attempts })
      })
      .catch((error) => {
        console.error('Deferred institutional access request failed', error)
        storeInstitutionalAccessRetry({ ...marker, attempts })
      })
  }, [access.isInstitutional, applyBusinessEmailMarker, refresh, registerVerify, requestInstitutionalAccess, user])

  const preferredUsernameRef = useRef(false)

  // Safety net for the business e-mail a registration could not write: apply it on
  // the first session that has the account, so a proven address is not left off.
  useEffect(() => {
    if (!isBrowser || preferredUsernameRef.current) return
    if (!user?.sub) return

    const marker = readPreferredUsernamePending()
    if (!marker || marker.userId !== user.sub) return

    if (user.preferred_username) {
      clearPreferredUsernamePending()
      return
    }

    preferredUsernameRef.current = true
    // Count the attempt before making it: a write that fails the same way every
    // time must stop retrying rather than run on every page load forever.
    applyBusinessEmailMarker(marker.email, { userId: marker.userId, attempts: marker.attempts + 1 })
  }, [applyBusinessEmailMarker, user])

  const ctx = useMemo(() => ({
    user,
    access,
    loading,
    hydrated,
    confirmEmail,
    confirmEmailNoSync,
    confirmCode,
    login,
    oAuthLogin,
    logout,
    requestInstitutionalAccess,
    registerVerify,
    signup,
    signupReset,
    resendSignupCode,
    forgotPasswordInit,
    forgotPassword,
    requestReset,
    confirmReset,
    verify,
    refresh,
    getUserFresh: sync,
    sync
  }), [user, access, loading, hydrated, confirmEmail, confirmEmailNoSync, confirmCode, login, oAuthLogin, logout, requestInstitutionalAccess, registerVerify, signup, signupReset, resendSignupCode, forgotPasswordInit, forgotPassword, requestReset, confirmReset, verify, refresh, sync])

  return (
    <AuthContext.Provider value={ctx}>
      {children}
      {loading ? <Loading /> : null}
    </AuthContext.Provider>
  )
}
